import { Filter, getStaticFilter } from '@/api/models';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { FilterFormData } from '@/pages/filters/hooks/useFilterMutations';
import { mdiDelete, mdiPencil } from '@mdi/js';
import * as React from 'react';
import { FilterForm } from './FilterForm';

export interface FilterDrawerViewProps {
  // State
  isOpen: boolean;
  filter: Filter | null;
  mode: 'view' | 'create' | 'edit';

  // Loading
  isLoading: boolean;
  isFormLoading: boolean;

  // Actions
  onClose: () => void;
  onEdit: (filter: Filter) => void;
  onDelete: (filter: Filter) => void;
  onFormSubmit: (data: FilterFormData) => Promise<void>;
}

export const FilterDrawerView: React.FC<FilterDrawerViewProps> = ({
  // State
  isOpen,
  filter,
  mode,

  // Loading
  isLoading,
  isFormLoading,

  // Actions
  onClose,
  onEdit,
  onDelete,
  onFormSubmit,
}) => {
  const staticFilter = filter ? getStaticFilter(filter.name) : null;

  const getTitle = () => {
    switch (mode) {
      case 'create':
        return 'Create New Filter';
      case 'edit':
        return filter
          ? `Edit ${staticFilter?.label || filter.name}`
          : 'Edit Filter';
      case 'view':
      default:
        return filter ? staticFilter?.label || filter.name : 'Filter Details';
    }
  };

  if (isLoading) {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} title='Loading...' width='md'>
        <div className='p-6 flex items-center justify-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title={getTitle()} width='md'>
      {mode === 'view' && filter && (
        <div className='p-6 space-y-6'>
          {/* Filter Header */}
          <div className='flex items-center space-x-4 pb-6 border-b border-gray-200'>
            <div className='w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-4xl'>
              {staticFilter?.emoji || '🏷️'}
            </div>
            <div className='flex-1'>
              <h3 className='text-xl font-semibold text-gray-900'>
                {staticFilter?.label || filter.name}
              </h3>
              <div className='text-sm text-gray-500'>{filter.name}</div>
              <div className='mt-2 flex items-center space-x-2'>
                {filter.enabled ? (
                  <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700'>
                    Enabled
                  </span>
                ) : (
                  <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700'>
                    Disabled
                  </span>
                )}
                {filter.isMandatory && (
                  <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700'>
                    Mandatory
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Filter Details */}
          <div className='grid grid-cols-1 gap-6'>
            <div className='bg-gray-50 rounded-lg p-4'>
              <h4 className='text-sm font-medium text-gray-700 mb-3'>
                Filter Information
              </h4>
              <dl className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <dt className='text-xs text-gray-500'>Filter ID</dt>
                  <dd className='text-sm font-mono text-gray-900'>
                    {filter.id}
                  </dd>
                </div>
                <div>
                  <dt className='text-xs text-gray-500'>Businesses Using</dt>
                  <dd className='text-sm text-gray-900'>
                    <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700'>
                      {filter.businessCount ?? 0}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className='text-xs text-gray-500'>Mandatory</dt>
                  <dd className='text-sm text-gray-900'>
                    {filter.isMandatory ? (
                      <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700'>
                        Yes
                      </span>
                    ) : (
                      <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700'>
                        No
                      </span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className='text-xs text-gray-500'>Created</dt>
                  <dd className='text-sm text-gray-900'>
                    {filter.createdAt
                      ? new Date(filter.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'N/A'}
                  </dd>
                </div>
                {filter.updatedAt && (
                  <div>
                    <dt className='text-xs text-gray-500'>Last Updated</dt>
                    <dd className='text-sm text-gray-900'>
                      {new Date(filter.updatedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Quick Actions */}
            <div className='bg-gray-50 rounded-lg p-4'>
              <h4 className='text-sm font-medium text-gray-700 mb-3'>
                Quick Actions
              </h4>
              <div className='flex flex-wrap gap-2'>
                <Button
                  icon={mdiPencil}
                  label='Edit Filter'
                  color='info'
                  small
                  onClick={() => onEdit(filter)}
                />
                <Button
                  icon={mdiDelete}
                  label='Delete Filter'
                  color='danger'
                  small
                  outline
                  onClick={() => onDelete(filter)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {(mode === 'create' || mode === 'edit') && (
        <FilterForm
          filter={filter}
          onSubmit={onFormSubmit}
          onCancel={onClose}
          loading={isFormLoading}
        />
      )}
    </Drawer>
  );
};
